import React, { useState } from 'react';
import { EvaluationResult, PredictionSegment, TranscriptionSegment } from '../types';
import { useTranslation } from 'react-i18next';

interface EvaluationResultsProps {
  evaluationResult: EvaluationResult;
  onSegmentClick: (start: number, end: number) => void;
  transcriptionSegments: TranscriptionSegment[] | null;
  isTranscribing: boolean;
}

export const EvaluationResults: React.FC<EvaluationResultsProps> = ({ evaluationResult, onSegmentClick, transcriptionSegments, isTranscribing }) => {
  const { t } = useTranslation();
  
  // Format time for segments
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Fonction pour obtenir la couleur du texte en fonction du score
  // (même code couleur que l'onde sonore : vert < 15, orange 15-30, rouge > 30)
  const getTextColorForScore = (score: number): string => {
    if (score > 30) return '#ff0000'; // Rouge
    if (score >= 15 && score <= 30) return '#ffa500'; // Orange
    return '#00cc00'; // Vert
  };

  // État pour gérer l'affichage des segments
  const [showAllSegments, setShowAllSegments] = useState<boolean>(false);
  
  // Segment sélectionné (index) pour afficher sa transcription
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
  
  // Nombre de segments à afficher par défaut
  const defaultVisibleSegments = 6;

  if (!evaluationResult || evaluationResult.prediction === null) {
    return null;
  }

  // Vérifier s'il y a plus de segments que le nombre par défaut
  const hasMoreSegments = evaluationResult.prediction_list && evaluationResult.prediction_list.length > defaultVisibleSegments;
  
  // Segments à afficher
  const visibleSegments = showAllSegments 
    ? evaluationResult.prediction_list 
    : evaluationResult.prediction_list?.slice(0, defaultVisibleSegments);

  // Extraire le texte transcrit couvrant la temporalité d'un segment d'évaluation
  const getTranscriptionForSegment = (segment: PredictionSegment): string => {
    if (!transcriptionSegments || transcriptionSegments.length === 0) {
      return '';
    }
    return transcriptionSegments
      .filter((tSeg) => tSeg.start < segment.end && tSeg.end > segment.start)
      .map((tSeg) => tSeg.text.trim())
      .filter((text) => text.length > 0)
      .join(' ');
  };

  // Texte du segment sélectionné (recalculé au rendu, sans hook conditionnel)
  const selectedSegment = selectedSegmentIndex !== null ? evaluationResult.prediction_list[selectedSegmentIndex] : null;
  const selectedTranscription = selectedSegment ? getTranscriptionForSegment(selectedSegment) : '';

  // Clic sur un segment : jouer l'audio et afficher la transcription
  const handleSegmentClick = (segment: PredictionSegment, index: number) => {
    onSegmentClick(segment.start, segment.end);
    setSelectedSegmentIndex(index);
  };

  return (
    <div className="card">
      <h2>{t('evaluation.resultsTitle')}</h2>
      
      {/* Scores par segment */}
      <div className="scores-container">
        <h3>{t('evaluation.segmentScoresTitle')}</h3>
        {evaluationResult.prediction_list && evaluationResult.prediction_list.length > 0 ? (
          <>
            {visibleSegments?.map((segment: PredictionSegment, index: number) => (
              <div 
                key={index} 
                className={`score-segment clickable${index === selectedSegmentIndex ? ' selected' : ''}`}
                onClick={() => handleSegmentClick(segment, index)}
              >
                <span>
                  {t('evaluation.segmentLabel', {
                    index: index + 1,
                    start: formatTime(segment.start),
                    end: formatTime(segment.end)
                  })}
                </span>
                <span style={{ color: getTextColorForScore(segment.prediction), fontWeight: 'bold' }}>
                  {segment.prediction.toFixed(2)}
                </span>
              </div>
            ))}
            
            {/* Bouton pour afficher plus/moins */}
            {hasMoreSegments && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <button 
                  className="button"
                  onClick={() => setShowAllSegments(!showAllSegments)}
                  style={{ backgroundColor: '#f5f5f5', color: 'rgb(72, 78, 82)', border: '1px solid #ddd' }}
                >
                  {showAllSegments ? t('evaluation.showLess') : t('evaluation.showMore')}
                </button>
              </div>
            )}
          </>
        ) : (
          <p style={{ textAlign: 'center', color: '#666' }}>
            {t('evaluation.noSegments')}
          </p>
        )}
      </div>
      
      {/* Transcription du segment sélectionné */}
      {selectedSegmentIndex !== null && selectedSegment && (
        <div className="transcription-panel" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
            {t('evaluation.transcriptionTitle', {
              index: selectedSegmentIndex + 1,
              start: formatTime(selectedSegment.start),
              end: formatTime(selectedSegment.end)
            })}
          </h4>
          {isTranscribing && !selectedTranscription ? (
            <p style={{ margin: 0, fontSize: '14px', color: '#888', fontStyle: 'italic' }}>
              {t('evaluation.transcriptionPending')}
            </p>
          ) : selectedTranscription ? (
            <p style={{ margin: 0, fontSize: '14px', color: 'rgb(72, 78, 82)' }}>
              {selectedTranscription}
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: '14px', color: '#888', fontStyle: 'italic' }}>
              {t('evaluation.transcriptionUnavailable')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
